import type { LucideIcon } from 'lucide-react';
import {
  Building,
  Briefcase,
  DoorOpen,
  Users,
  SlidersHorizontal,
  MessagesSquare,
  Map as MapIcon,
  Sparkles,
  Activity,
  BarChart3,
  Wrench,
  ListTodo,
  ShieldCheck,
  Landmark,
  FileText,
  BookOpen,
  Brain,
  Mail,
  BarChart2,
  Target,
  Settings,
  Home,
  Info,
  // Gauge,
} from 'lucide-react';
import { paths } from '@/routes/paths';
import type { AccessSpec } from '@/auth/tiers';
import type { FeatureKey } from '@/auth/features';

/**
 * The four sidebar states. `none` = the landing page (`/`) — no workflow chosen yet, so the rail
 * shows only the "Select your workflow" pill and the bottom block.
 */
export type Workflow = 'none' | 'ib' | 'research' | 'pe';

/** The three real workflows — everything that can be persisted or navigated to. */
export type ScoutVertical = Exclude<Workflow, 'none'>;

/**
 * localStorage key for the remembered workflow. Deliberately still `scout_vertical` even though the
 * type is now called `Workflow`: renaming the key would silently reset every existing session's
 * workflow on deploy. Only the three real workflows are ever written — `none` is runtime-only.
 */
export const VERTICAL_STORAGE_KEY = 'scout_vertical';

export interface SidebarNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Show only when can('admin:approve') / can('pe:admin'). */
  requireAdmin?: boolean;
  requirePeAdmin?: boolean;
  /**
   * Tier or capability gate (admin/role-based-access §2), checked **in addition** to the two
   * booleans above — it can only ever hide more, never reveal. Declaring nothing changes nothing.
   *
   * Must equal the router's resolved `handle.access` for `to`; `sidebarNav.access.test.ts` fails
   * CI if they disagree, which is what keeps the nav and the route tree from drifting apart.
   */
  access?: AccessSpec;
  /**
   * Rollout gate (§3c) — checked alongside `access`. Set on items whose page is a customer feature
   * that has not launched yet; launching is a value change in `auth/features.ts`, not here.
   */
  feature?: FeatureKey;
  /** Activity badge on the PE/IB Activity item. */
  activityBadge?: boolean;
  /** Pending-approvals count on this item. */
  approvalsBadge?: boolean;
  /** Navigate to Coming Soon instead of a live page. */
  comingSoon?: boolean;
}

export interface WorkflowMeta {
  /** Terse label used for `aria-label`s and nav headings. */
  label: string;
  /** Full Figma wording, shown in the workflow-switcher flyout. */
  menuLabel: string;
  icon: LucideIcon;
  /** Screen the workflow opens on. */
  firstPath: string;
  /** Hidden from the workflow switcher — the landing card stays visible but is not selectable. */
  comingSoon?: boolean;
}

export const WORKFLOW_META: Record<ScoutVertical, WorkflowMeta> = {
  ib: {
    label: 'IB',
    menuLabel: 'Investment Banking',
    icon: Landmark,
    firstPath: paths.ib.banks,
    comingSoon: true,
  },
  research: {
    label: 'Research',
    menuLabel: 'Buyer & Target Research',
    icon: Target,
    firstPath: paths.processPreference,
  },
  pe: {
    label: 'PE',
    menuLabel: 'Private Equity',
    icon: Building,
    firstPath: paths.pe.firms,
  },
};

/** Flyout order — IB first in the canonical list; coming-soon workflows are omitted from the pill. */
export const WORKFLOW_ORDER: ScoutVertical[] = ['ib', 'research', 'pe'];

/** Workflows the switcher pill may offer. */
export const SWITCHABLE_WORKFLOWS: ScoutVertical[] = WORKFLOW_ORDER.filter(
  (workflow) => !WORKFLOW_META[workflow].comingSoon,
);

/** @deprecated Use {@link WORKFLOW_META}. */
export const VERTICAL_META = WORKFLOW_META;

/** Prefixes that imply the Research vertical when no stored preference exists. */
export const RESEARCH_PREFIXES = [
  paths.processPreference,
  paths.targetList,
  paths.strategic,
  paths.financialVerticals,
  paths.previousResults,
  paths.knowledgeBank,
  paths.analystPreferences,
  paths.emailSync,
  paths.usage,
  paths.scorecard,
  paths.bdScoring,
];

/**
 * Bottom-block routes that belong to every rail (Deals, Organization, Settings, Admin Ops, FAQs).
 * Opening them must not switch the rail to Research — keep the rail the user arrived from,
 * including the landing `none` state.
 */
export const SHARED_PREFIXES = [paths.deals, '/org', '/settings', '/admin', paths.faq];

/** sessionStorage key for the currently displayed rail, including landing (`none`). */
export const RAIL_STORAGE_KEY = 'scout_rail';

function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function isSharedPath(pathname: string): boolean {
  return SHARED_PREFIXES.some((prefix) => pathMatchesPrefix(pathname, prefix));
}

/**
 * PE rail — Figma order (Exit Watch sits third, right after Holdings). Action Queue and Admin are
 * staff-only (`requirePeAdmin`) and are also listed under Admin Ops so they stay reachable from
 * other workflows.
 *
 * Two kinds of hidden item, deliberately distinguished (see auth/features.ts):
 *   • `feature` — a CUSTOMER feature not yet launched (Exit Watch, Ask the Market, Market Map)
 *   • `access: 'pe:admin'` — a PERMANENT staff tool (Digest, Activity, Analysis, Correction,
 *     Action Queue, Admin)
 */
export const PE_NAV: SidebarNavItem[] = [
  { to: paths.pe.firms, label: 'Firms', icon: Building },
  { to: paths.pe.holdings, label: 'Holdings', icon: Briefcase },
  // Customer features, held at staff-only rollout until launched (operator decision 2026-09-10).
  // `access` is the audience they WILL have; `feature` is whether they have shipped to it yet.
  // To launch: set the stage to 'all' in auth/features.ts — nothing here changes.
  {
    to: paths.pe.exitWatch,
    label: 'Exit Watch',
    icon: DoorOpen,
    access: 'pe:dataset',
    feature: 'pe.exitWatch',
  },
  { to: paths.pe.people, label: 'People', icon: Users },
  { to: paths.pe.screener, label: 'Screener', icon: SlidersHorizontal },
  {
    to: paths.pe.ask,
    label: 'Ask the Market',
    icon: MessagesSquare,
    access: 'pe:dataset',
    feature: 'pe.ask',
  },
  {
    to: paths.pe.marketMap,
    label: 'Market Map',
    icon: MapIcon,
    access: 'pe:dataset',
    feature: 'pe.marketMap',
  },
  // Permanent staff tools. `access` matches the route exactly (the parity test enforces it), so
  // these are hidden from the rail AND 404 on a typed URL — the §3b hole is closed.
  {
    to: paths.pe.digest,
    label: 'Digest',
    icon: Sparkles,
    requirePeAdmin: true,
    access: 'pe:admin',
  },
  {
    to: paths.pe.changes,
    label: 'Activity',
    icon: Activity,
    activityBadge: true,
    requirePeAdmin: true,
    access: 'pe:admin',
  },
  {
    to: paths.pe.analysis,
    label: 'Analysis',
    icon: BarChart3,
    requirePeAdmin: true,
    access: 'pe:admin',
  },
  {
    to: paths.pe.corrections,
    label: 'Correction',
    icon: Wrench,
    requirePeAdmin: true,
    access: 'pe:admin',
  },
  {
    to: paths.pe.actionQueue,
    label: 'Action Queue',
    icon: ListTodo,
    requirePeAdmin: true,
    access: 'pe:admin',
  },
  {
    to: paths.pe.admin,
    label: 'Admin',
    icon: ShieldCheck,
    requirePeAdmin: true,
    access: 'pe:admin',
  },
];

/**
 * IB rail. Activity reuses the shared `/pe/changes` feed. It used to be ungated here (IB users saw
 * it, PE users did not — a contradiction); as of 2026-09-10 Activity is staff-only on both rails.
 * `detectWorkflow` still keeps that route pinned to the stored workflow so opening it from IB does
 * not flip the rail to PE.
 */
export const IB_NAV: SidebarNavItem[] = [
  { to: paths.ib.banks, label: 'Bank', icon: Landmark },
  { to: paths.ib.transactions, label: 'Transactions', icon: FileText },
  { to: paths.ib.people, label: 'Professionals', icon: Users },
  { to: paths.ib.screener, label: 'IB Screener', icon: SlidersHorizontal },
  // Activity is the SAME route as the PE rail's, which is now staff-only (operator decision
  // 2026-09-10: Activity = staff forever). It was previously ungated here while PE hid it —
  // the two contradicted each other. Matched to the route, so IB no longer links to a 404.
  {
    to: paths.pe.changes,
    label: 'Activity',
    icon: Activity,
    activityBadge: true,
    access: 'pe:admin',
  },
];

/**
 * Research rail. "Research Home" (`/process-preference`) is absent per the Figma — it is still the
 * workflow's `firstPath`, so the way back to the wizard is the workflow pill's
 * "Buyer & Target Research" entry.
 */
export const RESEARCH_NAV: SidebarNavItem[] = [
  { to: paths.previousResults, label: 'Previous Results', icon: FileText },
  // { to: paths.scorecard, label: 'Scorecard', icon: Gauge },
  // Customer features, held at staff-only rollout until launched (operator decision 2026-09-15).
  // To launch either: set its stage to 'all' in auth/features.ts — nothing here changes.
  {
    to: paths.knowledgeBank,
    label: 'Knowledge Bank',
    icon: BookOpen,
    feature: 'research.knowledgeBank',
  },
  { to: paths.analystPreferences, label: 'My Preferences', icon: Brain },
  { to: paths.emailSync, label: 'Email Sync', icon: Mail, feature: 'research.emailSync' },
  // Platform-admin only (operator decision 2026-09-10) — matches the route gate.
  { to: paths.usage, label: 'API Usage', icon: BarChart2, access: 'platform' },
];

/** Permission gates a bottom-block entry can require. `orgNav` means `showOrgNav && orgNavSlug`. */
export type SidebarGate = 'org:manage_members' | 'admin:approve' | 'pe:admin' | 'orgNav';

export interface SidebarBottomChild {
  /** Static route, or a builder taking the org slug. */
  to: string | ((slug: string) => string);
  label: string;
  gate?: SidebarGate;
  /**
   * Rollout gate (§3c) — hidden until the feature ships, independently of `gate`. Note the knock-on
   * for a group with `overviewTo: null`: if every child is hidden the group renders nothing at all.
   */
  feature?: FeatureKey;
  /** Pending-approvals count (rendered on one item at a time — see SidebarBottomBlock). */
  approvalsBadge?: boolean;
}

export interface SidebarBottomGroup {
  key: string;
  label: string;
  icon: LucideIcon;
  /**
   * Route the parent row navigates to. `null` means "the first child the user can actually see",
   * so the parent never points at a route that would 403.
   */
  overviewTo: string | ((slug: string) => string) | null;
  children: SidebarBottomChild[];
  /** Hide the whole group unless this gate passes. */
  gate?: SidebarGate;
  /** Any one of these passing shows the group (used where two roles both need it). */
  gateAny?: SidebarGate[];
}

/**
 * The bottom block, identical in all four rail states — which is deliberate: Settings → Appearance
 * is the theme control, so it must never be stranded behind a workflow choice.
 * Order is Deals · Organization · Settings · Admin Ops · FAQs, with Log out rendered last by
 * SidebarBottomBlock (it is a button, not a route).
 */
export const BOTTOM_GROUPS: SidebarBottomGroup[] = [
  {
    key: 'deals',
    label: 'Deals',
    icon: Briefcase,
    overviewTo: paths.deals,
    // No sub-routes yet. The group shape is kept so children can be added without touching
    // Sidebar.tsx; SidebarGroup renders an empty group as a plain link (no dead chevron).
    children: [],
  },
  {
    key: 'organization',
    label: 'Organization',
    icon: Home,
    gate: 'orgNav',
    // Everyone lands on Dashboard now (it became member-visible on 2026-09-10); before that
    // managers landed there and everyone else on the CRM.
    overviewTo: null,
    // Operator decision 2026-09-10: a normal member sees Dashboard, Billing and Credit ledger.
    // Everything else is org-admin only. CRM and Domains stay in this list, held at `staff` in
    // auth/features.ts — platform admins still see them; launching is a value flip there.
    // Member-visible entries come FIRST so `overviewTo: null` lands a member on Dashboard.
    // Order and gating mirror ../orgSections.ts (the org page tab row) — orgSections.test.ts
    // asserts the two agree, because they are two lists, not one shared source.
    children: [
      { to: (slug) => paths.org.dashboard(slug), label: 'Dashboard' },
      { to: (slug) => paths.org.billing(slug), label: 'Billing' },
      { to: (slug) => paths.org.creditLedger(slug), label: 'Credit ledger' },
      { to: (slug) => paths.org.members(slug), label: 'Members', gate: 'org:manage_members' },
      { to: (slug) => paths.org.updates(slug), label: 'Updates', gate: 'org:manage_members' },
      { to: (slug) => paths.org.edit(slug), label: 'Edit', gate: 'org:manage_members' },
      { to: (slug) => paths.org.crm(slug), label: 'CRM', feature: 'org.crm' },
      {
        to: (slug) => paths.org.domains(slug),
        label: 'Domains',
        gate: 'org:manage_members',
        feature: 'org.domains',
      },
    ],
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: Settings,
    overviewTo: paths.settings.apiKeys,
    children: [
      { to: paths.settings.profile, label: 'Profile' },
      { to: paths.settings.apiKeys, label: 'API Keys' },
      // The light/dark control lives here.
      { to: paths.settings.appearance, label: 'Appearance' },
    ],
  },
  {
    key: 'admin',
    label: 'Admin Ops',
    icon: ShieldCheck,
    // A PE operator without `admin:approve` still needs the two PE staff tools.
    gateAny: ['admin:approve', 'pe:admin'],
    overviewTo: null,
    children: [
      {
        to: paths.admin.adminOps,
        label: 'Approvals',
        gate: 'admin:approve',
        approvalsBadge: true,
      },
      {
        to: paths.admin.pendingRegistrations,
        label: 'Pending Registrations',
        gate: 'admin:approve',
      },
      {
        to: paths.admin.organizationApprovals,
        label: 'Organization Approvals',
        gate: 'admin:approve',
      },
      { to: paths.admin.organizations, label: 'Organizations', gate: 'admin:approve' },
      { to: paths.admin.orgOps, label: 'Org Ops', gate: 'admin:approve' },
      { to: paths.admin.providerBudgets, label: 'Provider Budgets', gate: 'admin:approve' },
      // Also on the PE rail — kept here so IB / Research still reach the staff tools.
      { to: paths.pe.actionQueue, label: 'Action Queue', gate: 'pe:admin' },
      { to: paths.pe.admin, label: 'PE Admin Console', gate: 'pe:admin' },
    ],
  },
  {
    key: 'faq',
    label: 'FAQs',
    icon: Info,
    overviewTo: paths.faq,
    children: [],
  },
];

function readStoredVertical(): ScoutVertical | null {
  try {
    const stored = localStorage.getItem(VERTICAL_STORAGE_KEY) as ScoutVertical | null;
    if (stored === 'pe' || stored === 'ib' || stored === 'research') return stored;
  } catch {
    /* ignore */
  }
  return null;
}

function readStoredRail(): Workflow | null {
  try {
    const stored = sessionStorage.getItem(RAIL_STORAGE_KEY);
    if (stored === 'none' || stored === 'pe' || stored === 'ib' || stored === 'research') {
      return stored;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function writeStoredRail(workflow: Workflow): void {
  try {
    sessionStorage.setItem(RAIL_STORAGE_KEY, workflow);
  } catch {
    /* ignore */
  }
}

/**
 * Resolve the active workflow from the current path.
 * Activity (`/pe/changes`) is shared by PE and IB (same as Replit `/changes`) — for that
 * route we keep the stored workflow so IB → Activity does not flip the rail to PE.
 * Bottom-block pages (Deals / Organization / Settings / Admin Ops / FAQs) keep the rail the
 * user was already on, including the landing `none` state.
 */
export function detectWorkflow(pathname: string): Workflow {
  // The landing page owns no workflow. Checked first and by exact match — `'/'.startsWith` logic
  // would match every route.
  if (pathname === paths.landing) return 'none';

  const stored = readStoredVertical();
  const rail = readStoredRail();

  // Shared PE/IB Activity feed — respect the vertical the user was already in.
  if (pathname === paths.pe.changes || pathname.startsWith(`${paths.pe.changes}/`)) {
    if (stored === 'pe' || stored === 'ib') return stored;
    return 'pe';
  }

  if (pathname === paths.ib.banks || pathname.startsWith(`${paths.ib.banks}/`)) return 'ib';
  if (pathname.startsWith('/pe')) return 'pe';

  if (isSharedPath(pathname)) {
    if (rail !== null) return rail;
    if (stored) return stored;
    return 'none';
  }

  if (RESEARCH_PREFIXES.some((p) => pathMatchesPrefix(pathname, p))) {
    return 'research';
  }

  if (stored) return stored;
  return 'pe';
}

export function navIsActive(pathname: string, href: string): boolean {
  if (href === '/') return pathname === '/';
  // IB Banks is `/ib` — must not light when on /ib/transactions etc.
  if (href === paths.ib.banks) {
    const literals = [paths.ib.transactions, paths.ib.people, paths.ib.screener];
    if (literals.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return false;
    return pathname === paths.ib.banks || pathname.startsWith(`${paths.ib.banks}/`);
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}
